import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Download, Search } from "lucide-react";
import DashboardDataTable, {
  type DashboardTableColumn,
} from "src/components/common/DashboardDataTable";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { SelectMenu } from "src/dashboards/salesProductAnalytics/components/salesProductShared";
import type {
  PaidMediaOverviewCampaignRow,
  PaidMediaOverviewPlatformFilter,
  PaidMediaOverviewStatusOption,
} from "src/services/PaidMediaOverviewAPI";
import {
  formatOverviewAmount,
  formatOverviewDateLabel,
  formatOverviewMultiplier,
  formatOverviewNumber,
  formatOverviewPct,
  formatOverviewText,
  formatOverviewUnitCost,
} from "../../utils/paidMediaOverviewFormat";
import { overviewStatusLabel } from "../../utils/paidMediaOverviewLabels";
import { OVERVIEW_PLATFORM_DESCRIPTORS } from "../../data/paidMediaOverviewPlatforms";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { OverviewMetricTip, OverviewPlatformDot, OverviewStatusBadge } from "./overviewPrimitives";

const HEALTHY_ROAS = 4;

const ALL_OPTION_VALUE = "";

const LEVEL_TAB_OPTIONS: Array<{
  labelKey: string;
  platformLabelKey?: string;
  value: PaidMediaOverviewPlatformFilter;
}> = [
  { labelKey: "paidMediaOverview.leaderboard.tabAll", value: "all" },
  ...OVERVIEW_PLATFORM_DESCRIPTORS.map(descriptor => ({
    labelKey: "paidMediaOverview.leaderboard.tabPlatform",
    platformLabelKey: descriptor.labelKey,
    value: descriptor.key as PaidMediaOverviewPlatformFilter,
  })),
];

export interface CampaignLeaderboardFilters {
  platform: PaidMediaOverviewPlatformFilter;
  objectiveOrNetwork: string;
  status: string;
  search: string;
}

interface CampaignLeaderboardProps {
  rows: PaidMediaOverviewCampaignRow[];
  currency: string | null;
  filters: CampaignLeaderboardFilters;
  objectiveOptions: string[];
  statusOptions: PaidMediaOverviewStatusOption[];
  currentPage: number;
  totalPages: number;
  loading: boolean;
  exporting: boolean;
  onFiltersChange: (_patch: Partial<CampaignLeaderboardFilters>) => void;
  onPageChange: (_page: number) => void;
  onSelect: (_row: PaidMediaOverviewCampaignRow) => void;
  onExport: () => void;
}

const CampaignLeaderboard: FC<CampaignLeaderboardProps> = ({
  rows,
  currency,
  filters,
  objectiveOptions,
  statusOptions,
  currentPage,
  totalPages,
  loading,
  exporting,
  onFiltersChange,
  onPageChange,
  onSelect,
  onExport,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(filters.search);
  const debouncedQuery = useDebouncedValue(query);

  useEffect(() => {
    if (debouncedQuery !== filters.search) {
      onFiltersChange({ search: debouncedQuery });
    }
  }, [debouncedQuery, filters.search, onFiltersChange]);

  useEffect(() => {
    const styleId = "paid-media-overview-campaign-drawer-close";
    if (document.getElementById(styleId)) {
      return;
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent =
      ".ant-drawer-right .ant-drawer-header { flex-direction: row-reverse; }";
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    setQuery(current => (current === debouncedQuery ? filters.search : current));
  }, [filters.search, debouncedQuery]);

  const searchPending = query !== filters.search;
  const openSelectUpward = !loading && !searchPending && !rows?.length;

  const columns: Array<DashboardTableColumn<PaidMediaOverviewCampaignRow>> = [
    {
      key: "platform",
      header: (
        <HeaderCell
          tooltipId="colPlatform"
          formulaTitle={t("paidMediaOverview.leaderboard.helpFormulaTitle")}
        >
          {t("paidMediaOverview.leaderboard.columnPlatform")}
        </HeaderCell>
      ),
      render: row => <OverviewPlatformDot platform={row?.platform} />,
    },
    {
      key: "campaign_name",
      header: (
        <HeaderCell
          tooltipId="colCampaign"
          formulaTitle={t("paidMediaOverview.leaderboard.helpFormulaTitle")}
        >
          {t("paidMediaOverview.leaderboard.columnCampaign")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--strong",
      render: row => (
        <button
          type="button"
          className="paid-media-overview__leaderboard-row-link"
          aria-label={t("paidMediaOverview.leaderboard.rowAria", { name: row?.campaign_name })}
          onClick={() => onSelect(row)}
        >
          {formatOverviewText(row?.campaign_name)}
        </button>
      ),
    },
    {
      key: "objective_or_network",
      header: (
        <HeaderCell tooltipId="colObjective" formulaTitle={t("paidMediaOverview.leaderboard.helpFormulaTitle")}>
          {t("paidMediaOverview.leaderboard.columnObjective")}
        </HeaderCell>
      ),
      render: row => formatOverviewText(row?.objective_or_network),
    },
    {
      key: "start_date",
      header: (
        <HeaderCell tooltipId="colStartDate" formulaTitle={t("paidMediaOverview.leaderboard.helpFormulaTitle")}>
          {t("paidMediaOverview.leaderboard.columnStartDate")}
        </HeaderCell>
      ),
      render: row => formatOverviewDateLabel(row?.start_date),
    },
    {
      key: "spend",
      header: (
        <HeaderCell tooltipId="colSpend" align="right">
          {t("paidMediaOverview.metrics.spend")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => formatOverviewAmount(row?.spend, currency),
    },
    {
      key: "impressions",
      header: (
        <HeaderCell tooltipId="colImpressions" align="right">
          {t("paidMediaOverview.metrics.impressions")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => formatOverviewNumber(row?.impressions),
    },
    {
      key: "clicks",
      header: (
        <HeaderCell tooltipId="colClicks" align="right" formulaTitle={t("paidMediaOverview.leaderboard.helpFormulaTitle")}>
          {t("paidMediaOverview.metrics.clicks")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => formatOverviewNumber(row?.clicks),
    },
    {
      key: "ctr",
      header: (
        <HeaderCell tooltipId="colCtr" align="right">
          {t("paidMediaOverview.metrics.ctr")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => formatOverviewPct(row?.ctr, 2),
    },
    {
      key: "purchases",
      header: (
        <HeaderCell tooltipId="colPurchases" align="right">
          {t("paidMediaOverview.metrics.purchases")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => formatOverviewNumber(row?.purchases),
    },
    {
      key: "revenue",
      header: (
        <HeaderCell tooltipId="colRevenue" align="right">
          {t("paidMediaOverview.metrics.revenue")}
        </HeaderCell>
      ),
      className:
        "paid-media-overview__leaderboard-cell--right paid-media-overview__leaderboard-cell--strong",
      render: row => formatOverviewAmount(row?.revenue, currency),
    },
    {
      key: "roas",
      header: (
        <HeaderCell tooltipId="colRoas" align="right" formulaTitle={t("paidMediaOverview.leaderboard.helpFormulaTitle")}>
          {t("paidMediaOverview.metrics.roas")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => (
        <span
          className={
            typeof row?.roas === "number" && row.roas >= HEALTHY_ROAS
              ? "paid-media-overview__text-success paid-media-overview__cell-strong"
              : "paid-media-overview__cell-strong"
          }
        >
          {formatOverviewMultiplier(row?.roas)}
        </span>
      ),
    },
    {
      key: "cost_per_purchase",
      header: (
        <HeaderCell tooltipId="colCpp" align="right">
          {t("paidMediaOverview.metrics.costPerPurchase")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => formatOverviewUnitCost(row?.cost_per_purchase, currency),
    },
    {
      key: "spend_share",
      header: (
        <HeaderCell tooltipId="colSpendShare" align="right">
          {t("paidMediaOverview.metrics.spendShare")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => formatOverviewPct(row?.spend_share, 1),
    },
    {
      key: "revenue_share",
      header: (
        <HeaderCell tooltipId="colRevenueShare" align="right" formulaTitle={t("paidMediaOverview.leaderboard.helpFormulaTitle")}>
          {t("paidMediaOverview.metrics.revenueShare")}
        </HeaderCell>
      ),
      className: "paid-media-overview__leaderboard-cell--right",
      render: row => formatOverviewPct(row?.revenue_share, 1),
    },
    {
      key: "status",
      header: (
        <HeaderCell tooltipId="colStatus">
          {t("paidMediaOverview.leaderboard.columnStatus")}
        </HeaderCell>
      ),
      width: "112px",
      className: "paid-media-overview__leaderboard-cell--status",
      render: row => (
        <OverviewStatusBadge
          code={row?.status_code ?? null}
          label={row?.status_label ?? null}
          reason={row?.status_reason ?? null}
        />
      ),
    },
  ];

  const objectiveSelectOptions = [
    { label: t("paidMediaOverview.leaderboard.allObjectives"), value: ALL_OPTION_VALUE },
    ...(objectiveOptions ?? []).map(option => ({ label: option, value: option })),
  ];

  const statusSelectOptions = [
    { label: t("paidMediaOverview.leaderboard.allStatuses"), value: ALL_OPTION_VALUE },
    ...(statusOptions ?? []).map(option => ({
      label: overviewStatusLabel(t, option?.value, option?.label),
      value: option?.value ?? ALL_OPTION_VALUE,
    })),
  ];

  const renderTableBody = () => {

    if (loading) {
      return <LoadingSpinner compact />;
    }
    if (!rows?.length) {
      return (
        <p className="paid-media-overview__leaderboard-empty" role="status">
          {t("paidMediaOverview.leaderboard.emptyState")}
        </p>
      );
    }

    return (
      <DashboardDataTable
        columns={columns}
        rows={rows}
        rowKey={row => `${row?.platform}-${row?.campaign_id}`}
        currentPage={currentPage}
        totalPages={totalPages}
        onPreviousPage={() => onPageChange(Math.max(1, currentPage - 1))}
        onNextPage={() => onPageChange(Math.min(totalPages, currentPage + 1))}
      />
    );
  };

  return (
    <>
      <div
        className="paid-media-overview__level-tabs"
        role="tablist"
        aria-label={t("paidMediaOverview.leaderboard.tabsAria")}
      >
        {LEVEL_TAB_OPTIONS.map(option => {
          const selected = filters.platform === option.value;
          return (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onFiltersChange({ platform: option.value })}
              className={
                selected
                  ? "paid-media-overview__level-tab paid-media-overview__level-tab--active"
                  : "paid-media-overview__level-tab"
              }
            >
              {option.platformLabelKey
                ? t(option.labelKey, { platform: t(option.platformLabelKey) })
                : t(option.labelKey)}
            </button>
          );
        })}
      </div>

      <article className="paid-media-panel paid-media-overview__leaderboard campaign-leaderboard-article">
        <div className="paid-media-overview__leaderboard-header">
          <div className="paid-media-overview__leaderboard-toprow">
            <div className="paid-media-overview__panel-header-titles">
              <h3>{t("paidMediaOverview.leaderboard.title")}</h3>
              <p className="paid-media-overview__panel-subtitle">
                {t("paidMediaOverview.leaderboard.subtitle")}
              </p>
            </div>

            <div className="paid-media-overview__leaderboard-filters">
              <div className="paid-media-overview__leaderboard-search">
                <Search size={14} aria-hidden />
                <input
                  className="paid-media-overview__leaderboard-search-input"
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder={t("paidMediaOverview.leaderboard.searchPlaceholder")}
                  aria-label={t("paidMediaOverview.leaderboard.searchPlaceholder")}
                />
              </div>

              <SelectMenu
                options={objectiveSelectOptions}
                value={filters.objectiveOrNetwork}
                onChange={(value: string) => onFiltersChange({ objectiveOrNetwork: value })}
                className="paid-media-overview__leaderboard-filter-dropdown"
                ariaLabel={t("paidMediaOverview.leaderboard.objectiveFilterAria")}
                openUpward={openSelectUpward}
              />

              <SelectMenu
                options={statusSelectOptions}
                value={filters.status}
                onChange={(value: string) => onFiltersChange({ status: value })}
                className="paid-media-overview__leaderboard-filter-dropdown"
                ariaLabel={t("paidMediaOverview.leaderboard.statusFilterAria")}
                openUpward={openSelectUpward}
              />

              <button
                type="button"
                className="paid-media-overview__export-btn"
                onClick={onExport}
                disabled={!rows?.length || exporting}
                aria-label={t("paidMediaOverview.leaderboard.exportAria")}
              >
                <Download size={14} aria-hidden />
                {t("paidMediaOverview.leaderboard.export")}
              </button>
            </div>
          </div>
        </div>
        <div className="paid-media-overview__leaderboard-table-wrap">{renderTableBody()}</div>
      </article>
    </>
  );
};

const HeaderCell: FC<{ children: string; tooltipId: string; formulaTitle?: string; align?: "left" | "right" }> = ({
  children,
  tooltipId,
  formulaTitle,
  align = "left",
}) => (
  <span
    className={`paid-media-overview__leaderboard-th ${align === "right" ? "paid-media-overview__leaderboard-th--right" : ""
      }`.trim()}
  >
    {children}
    <OverviewMetricTip id={tooltipId} label={children} formulaTitle={formulaTitle} />
  </span>
);

export default CampaignLeaderboard;
